import React from "react";
import {
  Box,
  Typography,
  Divider,
  Card,
  CardContent,
  Grid,
  Chip,
} from "@mui/material";
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  DateRange as DateRangeIcon,
} from "@mui/icons-material";
import type { BossBabyChatSessionDto } from "@/apis/bossBabyChatLog";

interface UserInfoTabProps {
  session: BossBabyChatSessionDto;
}

/**
 * Boss Baby 사용자 정보 탭 컴포넌트
 *
 * @description Boss Baby 세션의 사용자 정보를 표시하는 탭
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-29
 */
export const UserInfoTab: React.FC<UserInfoTabProps> = ({ session }) => {
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        User Information
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={3}>
        {/* 사용자 기본 정보 */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <PersonIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography variant="h6">User Details</Typography>
              </Box>

              <Box sx={{ space: 2 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    User ID
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {session.userId || "Not specified"}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Session Status
                  </Typography>
                  <Chip
                    label={session.status}
                    color={
                      session.status === "active"
                        ? "success"
                        : session.status === "completed"
                        ? "primary"
                        : "default"
                    }
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 회사 정보 */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <BusinessIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography variant="h6">Company Information</Typography>
              </Box>

              <Box sx={{ space: 2 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Company ID
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {session.comId}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Company Name
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {session.companyName || "Not specified"}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 세션 통계 */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <DateRangeIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography variant="h6">Session Statistics</Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: "center", p: 2 }}>
                    <Typography variant="h4" color="primary.main" fontWeight="bold">
                      {session.messageCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Messages
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: "center", p: 2 }}>
                    <Typography variant="h4" color="success.main" fontWeight="bold">
                      {new Date(session.regDttm).toLocaleDateString("en-US")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Session Created
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: "center", p: 2 }}>
                    <Typography variant="h4" color="warning.main" fontWeight="bold">
                      {new Date(session.lastActivity).toLocaleDateString("en-US")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Last Activity
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: "center", p: 2 }}>
                    <Typography variant="h4" color="info.main" fontWeight="bold">
                      {session.sessionId}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Session ID
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};